import { ContractManagementDetailService } from '@/services/contractManagement/contractManagementDetailService';
import { toPartyRow } from '@/services/contractManagement/contractualPartyRow';
import {
  getContractManagementClause,
  getContractManagementObligation,
  getContractManagementMatter,
  updateContractManagementClause,
  deleteContractManagementClause,
  updateContractManagementObligation,
  deleteContractManagementObligation,
  updateContractManagementMatter,
  deleteContractManagementMatter,
  getContractManagementContractualParty,
  getContractualPartyRepresentedLegalEntities,
  deleteContractManagementContractualParty,
  type RelatedContractFilesResponse,
  type ContractManagementContractSearchResult,
  type ContractManagementClauseInDb,
  type ContractManagementObligationInDb,
  type ContractManagementMatterInDb,
  type ContractManagementContractualPartyLegalEntityInDb,
  type ContractManagementContractualPartyNaturalPersonInDb,
  type ContractManagementClauseUpdate,
  type ContractManagementObligationUpdate,
  type ContractManagementMatterUpdate,
  type FilterableSearchQuery,
} from '@/api';

/**
 * Contract Management Entity Types
 * Defines the different entity types in the contract management system
 */
export type ContractManagementEntityType =
  'clause' | 'obligation' | 'matter' | 'view' | 'contractual-party' | 'date';

/**
 * Union type for all contract management entity InDb types
 */
export type ContractManagementEntityInDb =
  | ContractManagementClauseInDb
  | ContractManagementObligationInDb
  | ContractManagementMatterInDb
  | ContractManagementContractualPartyNaturalPersonInDb
  | ContractManagementContractualPartyLegalEntityInDb;

/**
 * Union type for all contract management entity update types
 */
export type ContractManagementEntityUpdate =
  | ContractManagementClauseUpdate
  | ContractManagementObligationUpdate
  | ContractManagementMatterUpdate;

/**
 * Entity Detail Configuration
 * Configuration for each entity type's detail view
 * For matter, returns contracts instead of contract files
 */
export interface EntityDetailConfig {
  entityType: ContractManagementEntityType;
  displayName: string;
  routeName: string;
  listRouteName: string;
  // For matter, this returns ContractManagementContractSearchResult
  // For others, this returns RelatedContractFilesResponse
  getDetailFunction: (
    uid: string,
    searchQuery: FilterableSearchQuery,
    latestRevisionOnly?: boolean
  ) => Promise<RelatedContractFilesResponse | ContractManagementContractSearchResult>;
  getEntityFunction: (uid: string) => Promise<ContractManagementEntityInDb>;
  updateFunction?: (
    uid: string,
    data: ContractManagementEntityUpdate
  ) => Promise<ContractManagementEntityInDb>;
  deleteFunction?: (uid: string) => Promise<ContractManagementEntityInDb>;
  // Flag to indicate if this entity type shows contracts instead of contract files
  showsContracts?: boolean;
}

/**
 * Configuration for all contract management entity detail views
 * This centralizes the configuration and makes it easy to add new entity types
 */
export const entityDetailConfigs: Record<string, EntityDetailConfig> = {
  clause: {
    entityType: 'clause',
    displayName: 'Clause',
    routeName: 'contract-management-clause-detail',
    listRouteName: 'contract-management-clause',
    getDetailFunction: (
      uid: string,
      searchQuery: FilterableSearchQuery,
      latestRevisionOnly?: boolean
    ) =>
      ContractManagementDetailService.getClauseContractFiles(uid, searchQuery, latestRevisionOnly),
    getEntityFunction: (uid: string) =>
      getContractManagementClause({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    updateFunction: (uid: string, data: ContractManagementEntityUpdate) =>
      updateContractManagementClause({
        path: { uid },
        body: data,
      }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    deleteFunction: (uid: string) =>
      deleteContractManagementClause({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
  },
  obligation: {
    entityType: 'obligation',
    displayName: 'Obligation',
    routeName: 'contract-management-obligation-detail',
    listRouteName: 'contract-management-obligations',
    getDetailFunction: (
      uid: string,
      searchQuery: FilterableSearchQuery,
      latestRevisionOnly?: boolean
    ) =>
      ContractManagementDetailService.getObligationContractFiles(
        uid,
        searchQuery,
        latestRevisionOnly
      ),
    getEntityFunction: (uid: string) =>
      getContractManagementObligation({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    updateFunction: (uid: string, data: ContractManagementEntityUpdate) =>
      updateContractManagementObligation({
        path: { uid },
        body: data as ContractManagementObligationUpdate,
      }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    deleteFunction: (uid: string) =>
      deleteContractManagementObligation({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
  },
  matter: {
    entityType: 'matter',
    displayName: 'Matter',
    routeName: 'contract-management-matter-detail',
    listRouteName: 'contract-management-matter',
    getDetailFunction: (uid: string, searchQuery: FilterableSearchQuery) =>
      ContractManagementDetailService.getMatterContracts(uid, searchQuery),
    getEntityFunction: (uid: string) =>
      getContractManagementMatter({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    updateFunction: (uid: string, data: ContractManagementEntityUpdate) =>
      updateContractManagementMatter({
        path: { uid },
        body: data as ContractManagementMatterUpdate,
      }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    deleteFunction: (uid: string) =>
      deleteContractManagementMatter({ path: { uid } }).then(r => {
        if (!r.data) throw new Error('No data returned');
        return r.data;
      }),
    showsContracts: true, // Matter shows contracts, not contract files
  },
  // No updateFunction: a party's body depends on its kind and its representatives
  // sync separately, so it edits through the list config's `api.update`.
  'contractual-party': {
    entityType: 'contractual-party',
    displayName: 'Contractual Party',
    routeName: 'contract-management-contractual-party-detail',
    listRouteName: 'contract-management-contractual-party',
    getDetailFunction: (uid: string, searchQuery: FilterableSearchQuery) =>
      ContractManagementDetailService.getContractualPartyContracts(uid, searchQuery),
    // A person's companies come from their own endpoint, already filtered to
    // those the caller may see. ponytail: first 50 only, page it if a person
    // ever represents more.
    getEntityFunction: async (uid: string) => {
      const { data: party } = await getContractManagementContractualParty({
        path: { uid },
        throwOnError: true,
      });
      if (party.kind !== 'natural_person') return toPartyRow(party);
      const { data } = await getContractualPartyRepresentedLegalEntities({
        path: { uid },
        body: { text: '', limit: 50, offset: 0, sort_key: 'name', sort_direction: 'asc' },
        throwOnError: true,
      });
      return toPartyRow(party, data.results);
    },
    deleteFunction: (uid: string) =>
      deleteContractManagementContractualParty({ path: { uid }, throwOnError: true }).then(
        r => r.data
      ),
    // Linked per contract file, but listed by the contracts those files belong to.
    showsContracts: true,
  },
};

/**
 * Get configuration for a specific entity type
 */
export function getEntityDetailConfig(entityType: string): EntityDetailConfig | undefined {
  return entityDetailConfigs[entityType];
}
